'use client';

import { useGetUserProfile } from '@eos/event-management-data-access/generated/output';
import { Session } from 'next-auth';
import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { useInitializePendo } from '../../../utils/initializePendo';

export interface PendoClientProps {
  session: Session | null;
  host?: string;
}

export const PendoClient: React.FC<PendoClientProps> = ({
  session: serverSession,
  host,
}) => {
  const pathname = usePathname();

  // The server-rendered session prop is frozen at initial page load — the
  // shared [locale] layout (and this server component) doesn't re-run on a
  // client-side navigation. A guest who signs in on the verification screen and
  // is pushed to profile completion would otherwise keep the pre-auth (null)
  // session here. useSession() reads the live client session, which the
  // SessionProvider updates after signIn, so the verified guest identity is
  // available immediately without a refresh.
  const { data: clientSession, status } = useSession();
  const session = status === 'loading' ? serverSession : clientSession;
  // The guest verification screen identifies the visitor as a guest itself
  // (see GuestVerificationContainer). A coordinator may still have an active
  // session in the same browser, so we must NOT let the session-based init run
  // here — pendo.initialize() only takes effect once per page load, and the
  // coordinator identity would otherwise win over the guest's.
  const isGuestVerificationRoute =
    pathname?.includes('/guest-verification') ?? false;

  const effectiveSession = isGuestVerificationRoute ? null : session;
  const isCoordinator =
    effectiveSession?.user?.role?.includes('coordinator') ?? false;

  // Coordinators only: the profile supplies the organisation code for the
  // Pendo account. Never enabled for guests — a guest token's usr_id is not a
  // SOL user id. Shares the react-query cache with InvitationDialog.
  const { data: userProfile, isPending: isProfilePending } = useGetUserProfile({
    query: { enabled: isCoordinator },
  });

  // Coordinators wait for the profile query to settle (success or error); on
  // error they still initialize, just without an account.
  const ready = isCoordinator ? !isProfilePending : true;
  const organisationCode = isCoordinator
    ? (userProfile?.organisationCode ?? undefined)
    : effectiveSession?.user?.orgCode;

  useInitializePendo({
    session: effectiveSession,
    host,
    organisationCode,
    ready,
  });

  return null;
};
